import { useEffect, useState, Suspense, lazy } from "react"
import { Navigate, Route, Routes, useLocation } from "react-router-dom"
import { useAuthenticatedSession } from "./apis/auth"

const TechnicianView = lazy(() =>
    import("./pages/TechnicianView").then((m) => ({ default: m.TechnicianView }))
)
import Container from "@mui/material/Container"
import { useMsal } from "@azure/msal-react"
import { Box, Typography, useMediaQuery } from "@mui/material"
import { useTheme } from "@mui/material/styles"
import { backgroundColor } from "./colors"
import type { PathRoute } from "./routes"
import { ROUTES } from "./routes"
import { PATH_NAMES } from "./routes"
import useAxiosInterceptor from "./hooks/useAxiosInterceptor"
// import { HandheldProvider } from "./contexts/HandheldContext"
import Notifications from "./components/Notifications"
import { ReactQueryDevtools } from "@tanstack/react-query-devtools"
import Layout from "./components/Layout"
import { useGlobalState } from "./contexts/GlobalState"
import useGetCurrentUser from "./api/useGetCurrentUser"
import { useSnackbar } from "./contexts/SnackbarContext"
import { MessageConstant } from "./utils/MessageConst"

export default function App() {
    const { instance, accounts, inProgress } = useMsal()
    const { data: auth_session, isLoading: auth_session_loading } = useAuthenticatedSession()
    const ready = useAxiosInterceptor()
    const [loginAttempted, setLoginAttempted] = useState(false)
    const { state: globalState } = useGlobalState()
    const { data: currentUser, isLoading: currentUserLoading } = useGetCurrentUser(globalState?.userId || "")
    const { showSnackbar } = useSnackbar()
    const location = useLocation()
    const userRoles = currentUser?.userRoles ?? []
    const hasRoles = userRoles.length > 0
    const isHomeRoute = location.pathname === "/" || location.pathname === ""
    
    // On mobile resolution, render TechnicianView in-place (no URL change)
    // so resizing to desktop/tablet automatically restores the correct page
    const theme = useTheme()
    const isMobileResolution = useMediaQuery(theme.breakpoints.down('sm'))
    
    useEffect(() => {   
        if (accounts.length === 0 && inProgress === "none" && !loginAttempted) {            
            setLoginAttempted(true)
            instance.loginRedirect({
                scopes: [
                    "openid", 
                    "profile", 
                    "email",
                    "User.Read",
                    "User.Read.All",
                    "Group.Read.All"                    
                ]
            }).catch(error => {
                console.error("Login redirect failed:", error)
                setLoginAttempted(false) // Allow retry on error
            })
        }
    }, [accounts.length, inProgress, loginAttempted, instance])

    useEffect(() => {
        if (!currentUserLoading && currentUser && !hasRoles && !isHomeRoute) {
            showSnackbar({
                message: MessageConstant.NOPERMISSION,
                severity: "error",
                duration: 5000
            })
        }
    }, [currentUser, currentUserLoading, hasRoles, isHomeRoute, showSnackbar])

    // Show loading while MSAL is processing redirect
    if (inProgress !== "none") {
        return (
            <Box 
                display="flex" 
                alignItems="center" 
                justifyContent="center" 
                minHeight="100vh"
            >
                <Typography variant="h6">Processing authentication...</Typography>
            </Box>
        )
    }

    // If no accounts and login attempted, show loading
    if (accounts.length === 0) {
        return (
            <Box 
                display="flex" 
                alignItems="center" 
                justifyContent="center" 
                minHeight="100vh"
            >
                <Typography variant="h6">Redirecting to sign in...</Typography>
            </Box>
        )
    }

    return (
        <>
            {auth_session_loading || auth_session === undefined || !ready || (!isHomeRoute && currentUserLoading) ? (
                <Box 
                    display="flex" 
                    alignItems="center" 
                    justifyContent="center" 
                    minHeight="100vh"
                >
                    <Typography variant="h6">Loading user session...</Typography>
                </Box>           
            ) : (
                    <>
                    <Container
                        maxWidth={false}
                        disableGutters
                        style={{
                            marginTop: 0,
                            backgroundColor,
                            height: "calc(100vh - 64px)",
                            minHeight:0
                        }}
                    >                                                          
                         <Layout>                                     
                            {!hasRoles && !isHomeRoute ? (
                                <Navigate to="/" replace />
                            ) : (
                                <Routes>
                                    {ROUTES.map(({ path, Component }: PathRoute) => {
                                        const RouteComponent = isMobileResolution && path === PATH_NAMES.home ? TechnicianView : Component;
                                        return (
                                            <Route
                                                key={path}
                                                path={path}
                                                element={
                                                    <Suspense fallback={null}>
                                                        <RouteComponent />
                                                    </Suspense>
                                                }
                                            />
                                        );
                                    })}
                                </Routes>
                            )}
                        </Layout>  
                    </Container>
                    <Notifications />
                    <ReactQueryDevtools initialIsOpen={false} />
                    </>
                
            )}
        </>
    )
}
